import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, IconButton, Avatar } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../api';

import ChatSidebar from './ChatSidebar';
import ChatArena from './ChatArena';
import CreateChannelModal from './CreateChannelModal';
import ChannelSettingsModal from './ChannelSettingsModal'; 
import VoiceCallModal from './VoiceCallModal'; 

const ConnectPlus = () => {
  const [channels, setChannels] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  
  const [loadingChannels, setLoadingChannels] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [showRightPane, setShowRightPane] = useState(false); 

  // WebRTC Call State
  const [activeCall, setActiveCall] = useState(null);

  const ws = useRef(null);
  const activeChatIdRef = useRef(null); 

  const empId = localStorage.getItem('emp_id') || '';
  const safeEmpId = empId.trim().toUpperCase();
  const currentRole = localStorage.getItem('role');
  const isAdmin = ['Admin', 'Super Admin'].includes(currentRole);
  
let baseUrl = api.defaults.baseURL || `http://${window.location.hostname}:8000/api/v1`;
if (baseUrl.startsWith('/')) {
  baseUrl = window.location.origin + baseUrl;
}

  const safeChannels = Array.isArray(channels) ? channels : [];
  const safeMessages = Array.isArray(messages) ? messages : [];

  useEffect(() => { 
    activeChatIdRef.current = activeChat?.id; 
    setShowRightPane(false);
  }, [activeChat]);

  useEffect(() => {
    if (!safeEmpId) return;
    api.get(`/chat/channels/${safeEmpId}`)
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        setChannels(data);
        if (data.length > 0) setActiveChat(data[0]);
      })
      .catch(err => console.error("Failed to load channels", err))
      .finally(() => setLoadingChannels(false));
  }, [safeEmpId]);

  const markAsRead = (channelId) => {
    api.post(`/chat/channels/${channelId}/read/${safeEmpId}`).then(() => {
      setChannels(prev => (Array.isArray(prev) ? prev : []).map(c => c.id === channelId ? { ...c, unread_count: 0 } : c));
    }).catch(err => console.error(err));
  };

  const fetchMessages = (chatId, showLoading = true) => {
    if (showLoading) setLoadingMessages(true);
    api.get(`/chat/messages/${chatId}`)
      .then(res => setMessages(Array.isArray(res.data) ? res.data : []))
      .catch(err => console.error("Failed to load messages", err))
      .finally(() => setLoadingMessages(false));
  };

  useEffect(() => {
    if (activeChat) {
      fetchMessages(activeChat.id);
      markAsRead(activeChat.id); 
    }
  }, [activeChat]);

  // WS Connection with Strict React Mount Handling
  useEffect(() => {
    if (!safeEmpId) return;
    
    let reconnectTimer;
    let pingInterval;
    let isComponentMounted = true;

    const connectWS = () => {
      if (!isComponentMounted) return;

      const wsUrl = `${baseUrl.replace('http://', 'ws://').replace('https://', 'wss://')}/chat/ws/${safeEmpId}`;
      const socket = new WebSocket(wsUrl);
      ws.current = socket;

      socket.onopen = () => {
        pingInterval = setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'ping', sender_emp_id: safeEmpId }));
          }
        }, 25000);
      };

      socket.onmessage = (event) => {
        const incomingMsg = JSON.parse(event.data);
        if (incomingMsg.type === 'ping' || incomingMsg.type === 'pong') return;

        if (incomingMsg.isWebRTC) {
          if (incomingMsg.type === 'offer') {
            setActiveCall({
              targetId: incomingMsg.sender_emp_id ? incomingMsg.sender_emp_id.toString().trim().toUpperCase() : '',
              name: incomingMsg.caller_name || 'Colleague',
              isCaller: false,
              isVideo: incomingMsg.isVideo || false,
              signalPayload: incomingMsg
            });
          } else {
            setActiveCall(prev => prev ? { ...prev, signalPayload: incomingMsg } : null);
          }
          return; 
        }

        if (incomingMsg.channel_id === activeChatIdRef.current) {
          setMessages(prev => {
            const currentMsgs = Array.isArray(prev) ? prev : [];
            if (currentMsgs.some(m => m.id === incomingMsg.id)) return currentMsgs;
            return [...currentMsgs, incomingMsg];
          });
          markAsRead(activeChatIdRef.current); 
        } else {
          setChannels(prev => (Array.isArray(prev) ? prev : []).map(c => 
            c.id === incomingMsg.channel_id ? { ...c, unread_count: parseInt(c.unread_count || 0) + 1 } : c
          ));
        }
      };

      socket.onclose = () => {
        clearInterval(pingInterval);
        if (isComponentMounted) {
          reconnectTimer = setTimeout(connectWS, 3000);
        }
      };
    };

    connectWS();

    const fallbackInterval = setInterval(() => {
      if ((!ws.current || ws.current.readyState !== WebSocket.OPEN) && activeChatIdRef.current) {
        fetchMessages(activeChatIdRef.current, false);
      }
    }, 5000);

    return () => { 
      isComponentMounted = false;
      clearInterval(fallbackInterval);
      clearInterval(pingInterval);
      clearTimeout(reconnectTimer);
      
      if (ws.current) {
        const socket = ws.current;
        socket.onmessage = null; 
        
        // Strict Mode Fix: Never call .close() if it's currently CONNECTING
        if (socket.readyState === WebSocket.OPEN) {
          socket.onclose = null; // Prevent infinite reconnect loop on unmount
          socket.close();
        } else if (socket.readyState === WebSocket.CONNECTING) {
          // If caught mid-air, queue the closure for the exact moment it opens
          socket.onopen = () => {
            socket.onclose = null;
            socket.close();
          };
        }
      }
    };
  }, [safeEmpId, baseUrl]);

  const handleStartDM = async (targetEmployee) => {
    if (!targetEmployee) return;
    try {
      const res = await api.post('/chat/direct-message', {
        current_emp_id: safeEmpId, target_emp_id: targetEmployee.emp_id
      });
      const exists = safeChannels.find(c => c.id === res.data.id);
      if (!exists) setChannels(prev => [res.data, ...(Array.isArray(prev) ? prev : [])]);
      setActiveChat(res.data);
    } catch (err) { console.error("Failed to start chat", err); }
  };

  const handleChannelCreated = (newChannel) => {
    setChannels(prev => [newChannel, ...(Array.isArray(prev) ? prev : [])]);
    setActiveChat(newChannel);
  };

  const handleSendMessage = async (content = '', msgType = 'TEXT', fileUrl = null) => {
    if (!activeChat) return;
    if (msgType === 'TEXT' && !content.trim()) return;
    if (msgType === 'TEXT') setMessageInput(''); 
    
    try {
      const res = await api.post(`/chat/messages/${safeEmpId}`, {
        channel_id: activeChat.id, content: content.trim(), message_type: msgType, file_url: fileUrl
      });
      setMessages(prev => {
        const currentMsgs = Array.isArray(prev) ? prev : [];
        if (currentMsgs.some(m => m.id === res.data.id)) return currentMsgs;
        return [...currentMsgs, res.data];
      });
    } catch (err) {
      console.error("Failed to send message", err);
    }
  };

  const initiateCall = (targetId, name, isVideo) => {
    setActiveCall({
      targetId: targetId ? targetId.toString().trim().toUpperCase() : '',
      name: name,
      isCaller: true,
      isVideo: isVideo,
      signalPayload: null
    });
  };

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 80px)', width: '100%', bgcolor: '#d1d7db', overflow: 'hidden' }}>
      
      {activeCall && (
        <VoiceCallModal 
          ws={ws} 
          currentEmpId={safeEmpId} 
          callData={activeCall} 
          onEndCall={() => setActiveCall(null)}
          baseUrl={baseUrl}
        />
      )}

      <Box sx={{ width: '15%', minWidth: 250, maxWidth: 415, borderRight: '1px solid #d1d7db', bgcolor: '#fff', display: 'flex', flexDirection: 'column' }}>
        <ChatSidebar 
          channels={safeChannels} activeChat={activeChat} setActiveChat={setActiveChat} 
          loadingChannels={loadingChannels} onStartDM={handleStartDM}
          onOpenCreateModal={() => setCreateModalOpen(true)} safeEmpId={safeEmpId} baseUrl={baseUrl}
        />
      </Box>

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <ChatArena 
          activeChat={activeChat} messages={safeMessages} loadingMessages={loadingMessages}
          messageInput={messageInput} setMessageInput={setMessageInput} onSendMessage={handleSendMessage}
          safeEmpId={safeEmpId} baseUrl={baseUrl}
          onOpenSettings={() => {
            if (activeChat?.type === 'DIRECT') setShowRightPane(!showRightPane);
            else setSettingsModalOpen(true);
          }}
          onStartCall={initiateCall} 
        />
      </Box>

      {showRightPane && activeChat && (
        <Box sx={{ width: '30%', minWidth: 300, maxWidth: 415, borderLeft: '1px solid #d1d7db', bgcolor: '#f0f2f5', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ height: 60, bgcolor: '#f0f2f5', display: 'flex', alignItems: 'center', px: 2, gap: 2, borderBottom: '1px solid #e9edef' }}>
            <IconButton onClick={() => setShowRightPane(false)}><CloseIcon /></IconButton>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Contact Info</Typography>
          </Box>
          <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: '#fff', mb: 1 }}>
            <Avatar src={activeChat.target_emp_id ? `${baseUrl}/employees/${activeChat.target_emp_id}/profile-picture?t=${Date.now()}` : null} sx={{ width: 200, height: 200, mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 500 }}>{activeChat.name}</Typography>
            <Typography variant="body1" color="text.secondary">{activeChat.target_emp_id}</Typography>
          </Box>
          <Box sx={{ p: 2, bgcolor: '#fff' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>About</Typography>
            <Typography variant="body1">Available</Typography>
          </Box>
        </Box>
      )}

      <CreateChannelModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} onCreate={handleChannelCreated} currentEmpId={safeEmpId} isAdmin={isAdmin} />
      <ChannelSettingsModal 
        open={settingsModalOpen} onClose={() => setSettingsModalOpen(false)} channel={activeChat} currentEmpId={safeEmpId}
        onChannelDeleted={(id) => { setChannels(prev => (Array.isArray(prev) ? prev : []).filter(c => c.id !== id)); setActiveChat(null); }}
        onChannelUpdated={(updatedChannel) => { setChannels(prev => (Array.isArray(prev) ? prev : []).map(c => c.id === updatedChannel.id ? updatedChannel : c)); setActiveChat(updatedChannel); }}
      />
    </Box>
  );
};

export default ConnectPlus;